// eBay Post-Order API v2 — field-level coverage matrix data.
// Grounded in: server/src/channels/ebay/post-order.js (client),
// server/src/ai/enrichment.js + return-refund.js (read grounding),
// server/src/ai/actions.js + routes/ai.js (write execution), docs/ebay-apis.md.
// PERSISTENCE: nothing from Post-Order is written to Postgres — every field is a
// transient live lookup, so every `db` is null ("not stored").
export default {
  api: 'eBay Post-Order API v2',
  blurb:
    'Seller side of buyer-initiated disputes — cancellations, returns, MBG cases, and INR inquiries. Auth (as of 2026-07-21): the same user OAuth access token as the Message API (getAccessToken()), sent via the IAF scheme "Authorization: IAF <token>" — consolidated from the old EBAY_POST_ORDER_TOKEN secret. Reads gated by EBAY_POST_ORDER_READ_ENABLED; writes double-gated behind EBAY_POST_ORDER_WRITE_ENABLED. Nothing is persisted — all transient live lookups.',
  endpoints: [
    {
      verb: 'GET', path: '/cancellation/{cancelId}', fn: 'getCancellation()', rw: 'read',
      params: [{ p: 'cancelId (path)', req: 'required', send: 'y', val: 'eBay Cancel ID parsed from a FROM_EBAY system message' }],
      fields: [
        { f: 'cancelDetail.cancelReason', pull: true, db: null, note: 'Buyer reason; mapped to a TRQ reason code. Confirmed live', badge: 'pull' },
        { f: 'cancelDetail.cancelStatus', pull: true, db: null, note: 'CANCEL_CLOSED_WITH_REFUND short-circuits the approve flow', badge: 'pull' },
        { f: 'cancelDetail.legacyOrderId', pull: true, db: null, note: 'Preferred order id for the eBay eligibility check', badge: 'pull' },
        { f: 'cancelDetail.buyerLoginName', pull: true, db: null, note: 'Buyer id for the Trading-API notify message', badge: 'pull' },
        { f: 'cancelDetail.lineItems[].itemId', pull: true, db: null, note: 'First item id for the Trading-API notify message', badge: 'pull' },
      ],
    },
    {
      verb: 'POST', path: '/cancellation/check_eligibility', fn: 'checkCancellationEligibility()', rw: 'read',
      params: [{ p: 'legacyOrderId', req: 'required', send: 'y', val: 'legacyOrderId from getCancellation, else TRQ paymentId, else classifier orderId' }],
      fields: [{ f: 'eligible', pull: true, db: null, note: 'eligible===false hard-blocks cancel and flips cancelEligible in enrichOrder()', badge: 'pull' }],
    },
    {
      verb: 'POST', path: '/cancellation', fn: 'createCancellation()', rw: 'write',
      params: [
        { p: 'legacyOrderId', req: 'required', send: 'gated', val: 'eBay payment id (paymentId from TRQ)' },
        { p: 'cancelReason', req: 'required', send: 'gated', val: "eBay cancel reason code (e.g. 'BUYER_ASKED_CANCEL')" },
        { p: 'buyerLoginId', req: 'optional', send: 'gated', val: 'Included only when present' },
      ],
      fields: [{ f: 'cancelId', pull: false, db: null, note: 'Response not consumed by the executor (fire-and-log)', badge: 'gate' }],
    },
    {
      verb: 'POST', path: '/cancellation/{cancelId}/approve', fn: 'approveCancellation()', rw: 'write',
      params: [{ p: 'cancelId (path)', req: 'required', send: 'gated', val: 'eBay Cancel ID (executor calls with empty body {})' }],
      fields: [{ f: 'cancelId', pull: true, db: null, note: 'Path id we act on; approve_ebay_cancellation. Response body not read', badge: 'gate' }],
    },
    {
      verb: 'POST', path: '/cancellation/{cancelId}/reject', fn: 'rejectCancellation()', rw: 'write',
      params: [{ p: 'cancelId (path)', req: 'required', send: 'gated', val: 'eBay Cancel ID (executor calls with empty body {})' }],
      fields: [{ f: 'cancelId', pull: true, db: null, note: 'reject_ebay_cancellation when order already shipped. Response body not read', badge: 'gate' }],
    },
    {
      verb: 'GET', path: '/return/{returnId}', fn: 'getReturn()', rw: 'read',
      params: [{ p: 'returnId (path)', req: 'required', send: 'y', val: 'From searchReturnByOrderId / getCase' }],
      fields: [
        { f: 'summary.creationInfo.item.itemId', pull: true, db: null, note: 'Matched to TRQ item via ebay_id attribute. Confirmed live', badge: 'pull' },
        { f: 'summary.status / summary.state', pull: true, db: null, note: 'Confirmed present in the live summary block', badge: 'pull' },
        { f: 'summary.creationInfo.item.transactionId', pull: false, db: null, note: 'Present in confirmed shape; not currently read', badge: 'avail' },
        { f: 'reason', pull: false, db: null, note: 'Code reads returnData?.reason but the path is unconfirmed (likely detail.itemDetail)', badge: 'verify' },
        { f: 'buyerComments / comments', pull: false, db: null, note: 'Read for partial-refund detection; nested path not confirmed', badge: 'verify' },
        { f: 'lineItems[].quantity / quantity', pull: false, db: null, note: 'Read as returnQuantity (defaults 1); path unconfirmed', badge: 'verify' },
      ],
    },
    {
      verb: 'GET', path: '/return/search', fn: 'searchReturnByOrderId()', rw: 'read',
      params: [{ p: 'order_id', req: 'required', send: 'y', val: 'eBay order id (paymentId from TRQ)' }],
      fields: [
        { f: 'members[].returnId', pull: true, db: null, note: 'members[0].returnId → existingReturnId (Flow A vs B). Confirmed live', badge: 'pull' },
        { f: 'members[].creationInfo.item.{itemId,transactionId}', pull: false, db: null, note: 'Present; itemId re-read later via getReturn', badge: 'avail' },
        { f: 'total / paginationOutput / countSummary', pull: false, db: null, note: 'Confirmed present; not read', badge: 'avail' },
      ],
    },
    {
      verb: 'POST', path: '/return/{returnId}/decide', fn: 'decideReturn()', rw: 'write',
      params: [
        { p: 'decision', req: 'required', send: 'gated', val: "'APPROVE' | 'DECLINE'" },
        { p: 'RMANumber / rMAProvided', req: 'optional', send: 'gated', val: 'RMA path (erpRmaId, rMAProvided:true)' },
        { p: 'keepOriginalItem', req: 'optional', send: 'gated', val: 'true — scrap-refund path (approve without item back)' },
      ],
      fields: [{ f: 'returnId', pull: true, db: null, note: 'Path id acted on; RMA + scrap-refund branches. Response body not read', badge: 'gate' }],
    },
    {
      verb: 'POST', path: '/return/{returnId}/issue_refund', fn: 'issueReturnRefund()', rw: 'write',
      params: [
        { p: 'refundDetail.itemizedRefundDetail[]', req: 'required', send: 'gated', val: '[{ refundAmount:{value,currency:USD}, refundFeeType:PURCHASE_PRICE }]' },
        { p: 'refundDetail.totalAmount', req: 'required', send: 'gated', val: '{ value: orderTotal, currency: USD }' },
      ],
      fields: [{ f: 'returnId', pull: true, db: null, note: 'Scrap-refund path refunds the full orderTotal. Response body not read', badge: 'gate' }],
    },
    {
      verb: 'POST', path: '/return/{returnId}/file/upload', fn: 'uploadReturnFile()', rw: 'write',
      params: [{ p: 'fileName / filePurpose / data', req: 'required', send: 'gated', val: "defaults filePurpose: LABEL_RELATED — data is the TRQ-recovered label image" }],
      fields: [{ f: 'returnId', pull: true, db: null, note: 'RMA path and resend_return_label Flow B-create upload the recovered label image ahead of add_shipping_label; response fileId feeds that call', badge: 'gate' }],
    },
    {
      verb: 'POST', path: '/return/{returnId}/add_shipping_label', fn: 'addShippingLabel()', rw: 'write',
      params: [{ p: 'carrier / labelAction / trackingNumber / fileId / comments', req: 'optional', send: 'gated', val: "defaults UPS / MARK_AS_SENT — RMA path and resend_return_label Flow B-create use labelAction: UPLOAD_LABEL with the uploaded fileId + a buyer-facing comments note" }],
      fields: [{ f: 'returnId', pull: true, db: null, note: 'RMA path and resend_return_label Flow B-create tell eBay the uploaded file IS this return\'s label', badge: 'gate' }],
    },
    {
      verb: 'GET', path: '/casemanagement/{caseId}', fn: 'getCase()', rw: 'read',
      params: [{ p: 'caseId (path)', req: 'required', send: 'y', val: 'Case ID from message / grounding' }],
      fields: [
        { f: 'returnId (or caseData.returnId)', pull: true, db: null, note: 'Bridges a case to its return in routes/ai.js', badge: 'pull' },
        { f: 'itemId', pull: true, db: null, note: 'Read in the executor case branch', badge: 'pull' },
        { f: 'caseType', pull: true, db: null, note: 'Read in the executor case branch', badge: 'pull' },
      ],
    },
    {
      verb: 'GET', path: '/casemanagement/search', fn: 'searchCaseByOrderId()', rw: 'read',
      params: [{ p: 'order_id', req: 'required', send: 'y', val: 'eBay order id (paymentId from TRQ)' }],
      fields: [
        { f: 'members[].caseId', pull: true, db: null, note: 'First caseId → ebayCaseOrInquiry (case takes priority over inquiry)', badge: 'pull' },
        { f: 'members[].caseStatusEnum', pull: true, db: null, note: 'Surfaced as open-case status in the order-facts block', badge: 'pull' },
      ],
    },
    {
      verb: 'GET', path: '/inquiry/{inquiryId}', fn: 'getInquiry()', rw: 'read',
      params: [{ p: 'inquiryId (path)', req: 'required', send: 'y', val: 'eBay Request ID from a FROM_EBAY INR message' }],
      fields: [
        { f: 'inquiryReasonCode / inquiryType', pull: true, db: null, note: 'Surfaced as inquiry reason in the INR preview', badge: 'pull' },
        { f: 'itemId / lineItems[].itemId', pull: true, db: null, note: 'Matched to TRQ item via ebay_id for sku/tracking', badge: 'pull' },
        { f: 'inquiryHistoryDetails.historyActions[]', pull: true, db: null, note: 'Count drives Branch A (==2) vs B (follow-up); mines seller/buyer address exchange', badge: 'pull' },
        { f: 'inquiryQuantity', pull: true, db: null, note: 'Quantity for the replacement/refund body (defaults 1)', badge: 'pull' },
        { f: 'inquiryHistoryDetails.buyerrequested', pull: true, db: null, note: 'Branch B: parsed for "full refund" vs "still wants the item"', badge: 'pull' },
      ],
    },
    {
      verb: 'GET', path: '/inquiry/search', fn: 'searchInquiryByOrderId()', rw: 'read',
      params: [{ p: 'order_id', req: 'required', send: 'y', val: 'eBay order id; fallback when no open case' }],
      fields: [
        { f: 'members[].inquiryId', pull: true, db: null, note: 'First inquiryId → ebayCaseOrInquiry (fallback after case search)', badge: 'pull' },
        { f: 'members[].inquiryStatusEnum', pull: true, db: null, note: 'Surfaced as open-INR status in the order-facts block', badge: 'pull' },
      ],
    },
    {
      verb: 'POST', path: '/inquiry/{inquiryId}/issue_refund', fn: 'issueInquiryRefund()', rw: 'write',
      params: [{ p: 'comments', req: 'optional', send: 'gated', val: '{ content, language:"en" } — fixed refund confirmation' }],
      fields: [{ f: 'inquiryId', pull: true, db: null, note: 'Branch B refund; paired with a TRQ createReturn (code 92)', badge: 'gate' }],
    },
    {
      verb: 'POST', path: '/inquiry/{inquiryId}/send_message', fn: 'sendInquiryMessage()', rw: 'write',
      params: [{ p: 'message.content', req: 'required', send: 'gated', val: 'Reply text (delivered/in-processing message, Branch A)' }],
      fields: [{ f: 'inquiryId', pull: true, db: null, note: 'Branch A buyer message. Response body not read', badge: 'gate' }],
    },
    {
      verb: 'POST', path: '/inquiry/{inquiryId}/provide_shipment_info', fn: 'provideShipmentInfo()', rw: 'write',
      params: [
        { p: 'trackingNumber', req: 'required', send: 'gated', val: 'TRQ order tracking number (throws if absent)' },
        { p: 'proofOfShipmentUploaded / shippedWithTracking', req: 'required', send: 'gated', val: 'true' },
        { p: 'sellerComments', req: 'optional', send: 'gated', val: '{ content, language:"en" } — in-transit message' },
      ],
      fields: [{ f: 'inquiryId', pull: true, db: null, note: 'Branch A shipment-proof path (shipped, not yet delivered)', badge: 'gate' }],
    },
    {
      verb: 'POST', path: '/inquiry/{inquiryId}/close · /return/{id}/send_message', fn: 'closeInquiry / sendReturnMessage', rw: 'write',
      params: [{ p: 'payload', req: 'required', send: 'gated', val: 'Pass-through bodies' }],
      fields: [{ f: '(none read)', pull: false, db: null, note: 'Exported client methods; not wired into a current flow', badge: 'gate' }],
    },
    {
      verb: 'GET', path: '/return (searchReturns) · /return/search (byTransaction)', fn: 'searchReturns / searchReturnByTransaction', rw: 'read',
      params: [{ p: 'buyerLoginId / orderId / transaction_id / item_id', req: 'optional', send: 'y', val: 'Query params' }],
      fields: [{ f: '(members[] etc.)', pull: false, db: null, note: 'Exported but not wired into a current flow (searchReturnByOrderId used instead)', badge: 'avail' }],
    },
  ],
}
